import React, { useCallback, useEffect, useState } from 'react'
import DocumentHeader from '../../../../documents/components/DocumentHeader'
import CommonInvoiceTemplate from '../../../../documents/components/CommonInvoiceTemplate'
import { SinglePurchaseReturnData, TableColumn } from '../../../../types'
import { useParams } from 'react-router-dom'
import { Box } from '@mui/material'
import { request } from '../../../../../../utils/request'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import PrintButtons from '../../../../../../components/PrintButtons'
import usePrint from '../../../../../../utils/usePrint'
import PrintableContainer from '../../../../../../components/PrintableContainer'
import { BusinessInfo, TaxInfo } from '../../../../../controlPanel/types'

const ShowPurchaseReturn = () => {
  const [loading, setLoading] = useState(true);
  const [singlePurchaseReturn, setSinglePurchaseReturn] = useState<SinglePurchaseReturnData | null>(null);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>();
  const [taxInfo, setTaxInfo] = useState<TaxInfo>();
  const params = useParams()
  const purchaseReturnId = params.purchaseReturnId

  const { isPrinting, handlePrint } = usePrint();

  const fetchBusinessInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/businessInfo",
        method: "get",
      });

      if (res.success) {
        setBusinessInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch business info");
    }
  }, []);
  useEffect(() => {
    fetchBusinessInfo();
  }, [fetchBusinessInfo])

  const fetchTaxInfo = useCallback(async () => {
    try {
      const res = await request({
        url: "setting/taxInfo",
        method: "get",
      });

      if (res.success) {
        setTaxInfo(res.data);
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to fetch tax info");
    }
  }, []);
  useEffect(() => {
    fetchTaxInfo();
  }, [fetchTaxInfo])


  const fetchSinglePurchaseReturn = useCallback(async () => {
    if (!purchaseReturnId) return;

    setLoading(true)
    try {
      const res = await request({
        url: `purchaseReturn/get-single-purchase-return/${purchaseReturnId}`,
        method: "get",
      })
      if (res.success && res.data) {
        setSinglePurchaseReturn(res.data)
      } else {
        toast.error(res.message || "Failed to fetch Purchase Return ");
      }
    } catch (error: any) {
      toast.error(error.message || "Error fetching Purchase Return ");
    } finally {
      setLoading(false)
    }
  }, [purchaseReturnId])

  useEffect(() => {
    fetchSinglePurchaseReturn();
  }, [fetchSinglePurchaseReturn])

  // Format date to readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('DD MMM YYYY');
  };

  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('purchase_return_print', {
          title: `Purchase Return - ${singlePurchaseReturn?.purchaseReturnNumber}'}`,
          fileName: `Purchase _${singlePurchaseReturn?.purchaseReturnNumber}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
          .invoice-heading {text-align:center; font-size: 22px}
          .flex-center {display:flex; justify-content:space-between; align-items: flex-start; gap:5px}
          .sub-heading {font-size:14px; font-weight:bold}
          .text-bold {font-weight:bold}
          .w-50{width:50%}
          .out-border{border:1px solid black}
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  // Calculate totals from invoice items
  const calculateTotals = () => {
    if (!singlePurchaseReturn?.items?.length) {
      return {
        quantity: 0,
        taxableAmount: 0,
        tax: 0,
        amount: 0
      };
    }

    const totals = singlePurchaseReturn.items.reduce((acc, item) => {
      acc.quantity += item.quantity || 0;
      acc.amount += item.amount || 0;
      // Calculate tax amount based on item amount and tax rate
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      acc.tax += taxAmount;
      // Taxable amount is amount minus tax (for GST calculations)
      acc.taxableAmount += (item.amount || 0) - taxAmount;
      return acc;
    }, { quantity: 0, taxableAmount: 0, tax: 0, amount: 0 });

    return totals;
  };

  const prepareTableData = () => {
    if (!singlePurchaseReturn?.items?.length) return [];

    return singlePurchaseReturn.items.map((item, index) => {
      const taxAmount = ((item.amount || 0) * (item.tax?.rate || 0)) / 100;
      return {
        id: item.id || index.toString(),
        item: item.itemName || `Item ${index + 1}`,
        hsnNo: item.hsnNo || 'N/A',
        quantity: item.quantity || 0,
        rate: item.price || 0,
        tax: taxAmount,
        taxPercentage: item.tax?.rate || 0,
        amount: item.amount || 0,
        discount: item.discount?.amount || 0,
        discountPercentage: item.discount?.isPercentage ? item.discount.amount : 0
      };
    });
  };

  // Prepare table columns based on GST type
  const prepareTableColumns = (): TableColumn[] => {
    const baseColumns: TableColumn[] = [
      // { id: 'srNo', label: 'Sr No', align: "left", width: 60 },
      { id: 'item', label: 'Item Description', align: "left", width: 200 },
      { id: 'quantity', label: 'Qty', align: "right", width: 80 },
      { id: 'rate', label: 'Rate (₹)', align: "right", width: 100 },
      { id: 'discount', label: 'Discount (₹)', align: "right", width: 100 },
    ];

    if (singlePurchaseReturn?.gstType === 'GST') {
      // baseColumns.push({ id: 'hsnNo', label: 'HSN/SAC', align: "left", width: 100 });
    }

    baseColumns.push(
      // { id: 'taxPercentage', label: 'Tax %', align: "right", width: 80 },
      { id: 'tax', label: 'Tax Amt (₹)', align: "right", width: 100 },
      { id: 'amount', label: 'Amount (₹)', align: "right", width: 120 }
    );

    return baseColumns;
  };

  // Prepare summary items
  const prepareSummaryItems = () => {
    if (!singlePurchaseReturn) return [];

    const totals = calculateTotals();
    const summaryItems = [];

    // Taxable Amount
    summaryItems.push({
      label: 'Taxable Amount',
      value: singlePurchaseReturn?.taxableAmount,
      variant: 'body2' as const
    });

    // GST Breakdown if GST Sales Return 
    if (singlePurchaseReturn.gstType === 'GST' && singlePurchaseReturn.taxBreakdown?.length > 0) {
      singlePurchaseReturn.taxBreakdown.forEach((taxItem, index) => {
        if (taxItem.cgst > 0) {
          summaryItems.push({
            label: `CGST (${taxItem.cgst}%)`,
            value: (totals.taxableAmount * taxItem.cgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.sgst > 0) {
          summaryItems.push({
            label: `SGST (${taxItem.sgst}%)`,
            value: (totals.taxableAmount * taxItem.sgst) / 100,
            variant: 'body2' as const
          });
        }
        if (taxItem.igst > 0) {
          summaryItems.push({
            label: `IGST (${taxItem.igst}%)`,
            value: (totals.taxableAmount * taxItem.igst) / 100,
            variant: 'body2' as const
          });
        }
      });
    } else if (singlePurchaseReturn.gstType === 'GST') {
      // Fallback if no tax breakdown
      summaryItems.push({
        label: `GST (${singlePurchaseReturn.items[0]?.tax?.rate || 0}%)`,
        value: totals.tax,
        variant: 'body2' as const
      });
    }

    // Additional Charges
    if (singlePurchaseReturn.charges?.length > 0) {
      singlePurchaseReturn.charges.forEach(charge => {
        summaryItems.push({
          label: charge.label,
          value: charge.amount,
          variant: 'body2' as const
        });
      });
    }

    // Discount
    if (singlePurchaseReturn.discount?.amount && singlePurchaseReturn.discount.amount > 0) {
      const discountLabel = singlePurchaseReturn.discount.isPercentage
        ? `Discount (${singlePurchaseReturn.discount.amount}%)`
        : 'Discount';
      summaryItems.push({
        label: discountLabel,
        value: singlePurchaseReturn.discount.amount,
        variant: 'body2' as const
      });
    }

    // Round Off
    if (singlePurchaseReturn.roundOff) {
      const roundOffValue = Math.round(totals.amount + totals.tax) - (totals.amount + totals.tax);
      if (Math.abs(roundOffValue) > 0) {
        summaryItems.push({
          label: 'Round Off',
          value: roundOffValue,
          variant: 'body2' as const
        });
      }
    }

    // Grand Total
    const grandTotal = singlePurchaseReturn.totalAmount;
    summaryItems.push({
      label: 'Grand Total',
      value: grandTotal,
      bold: true,
      variant: 'body1' as const
    });

    return summaryItems;
  };

  // Prepare custom fields for payment info
  const prepareCustomFields = () => {
    if (!singlePurchaseReturn) return [];

    const fields = [];

    if (singlePurchaseReturn.receivedAmount >= 0) {
      fields.push({
        label: "Paid Amount",
        value: singlePurchaseReturn.receivedAmount
      });
    }

    if (singlePurchaseReturn.balanceAmount >= 0) {
      fields.push({
        label: "Balance Due",
        value: singlePurchaseReturn.balanceAmount
      });
    }

    return fields;
  };

  if (!singlePurchaseReturn && !loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <DocumentHeader
          heading='Sales Return'
          subtitle='Sales Return not found'
          showBackButton={true}
        />
      </Box>
    );
  }

  const totals = calculateTotals();
  const tableData = prepareTableData();
  const tableColumns = prepareTableColumns();
  const summaryItems = prepareSummaryItems();
  const customFields = prepareCustomFields();

  return (
    <>
      <DocumentHeader
        heading='Purchase Return'
        subtitle={`#${singlePurchaseReturn?.purchaseReturnNumber || 'Loading...'}`}
        status={singlePurchaseReturn?.status || 'UNPAID'}
        statusColor={singlePurchaseReturn?.status === 'PAID' ? 'success' :
          singlePurchaseReturn?.status === 'PARTIAL_PAID' ? 'info' : singlePurchaseReturn?.status === 'UNPAID' ? 'warning' : 'primary'}
        showBackButton={true}
      />

      <Box display='flex' justifyContent='flex-end' mb={3} gap={2}>

        <PrintButtons
          onPrint={onPrint}
          isPrinting={isPrinting}
          printLabel='Print PR'
          pdfLabel='Export PDF'
          disabled={loading || !singlePurchaseReturn}
          showPDF={false} // Remove PDF button since we don't have export functionality yet
        />
        {/* <CommonCreateButton
          label="Record Payment"
          onClick={() => alert('Record Payment clicked')}
          disabled={singlePurchaseReturn?.status === 'PAID'}
        /> */}
      </Box>

      <PrintableContainer id="purchase_return_print">
        <CommonInvoiceTemplate
          documentType={singlePurchaseReturn?.purchaseReturnType || "PURCHASE_RETURN"}
          title={singlePurchaseReturn?.gstType === 'GST' ? "PURCHASE RETURN" : "PURCHASE RETURN"}

          companyName={businessInfo?.businessName}
          companyAddress={
            (businessInfo?.address) ? (`${businessInfo.address?.[0]?.line1} ${businessInfo.address?.[0]?.city} ${businessInfo.address?.[0]?.state}  ${businessInfo.address?.[0]?.pinCode}`) : 'Address not mentioned'
          }
          companyPhone={(businessInfo?.contact?.phone?.[0]?.phoneNo) ? (`${businessInfo?.contact?.phone?.[0]?.phoneNo}`) : '+91 0000000000'}
          companyGSTIN={singlePurchaseReturn?.gstType === 'GST' ? taxInfo?.taxDetails?.gstNumber : ""}

          documentNumber={singlePurchaseReturn?.purchaseReturnNumber || ''}
          documentDate={formatDate(singlePurchaseReturn?.purchaseReturnDate || singlePurchaseReturn?.date || '')}
          dueDate={formatDate(singlePurchaseReturn?.dueDate || '')}
          partyHeading='Vendor/Party Info.'
          partyName={singlePurchaseReturn?.party?.partyName || singlePurchaseReturn?.vendor?.vendorName || 'N/A'}
          partyLocation={singlePurchaseReturn?.party?.location || singlePurchaseReturn?.vendor?.location}
          partyAddress={
            (singlePurchaseReturn?.party?.address) ? (`${singlePurchaseReturn.party.address.line1} ${singlePurchaseReturn.party.address.city} ${singlePurchaseReturn.party.address.state}  ${singlePurchaseReturn.party.address.pinCode}`) : (singlePurchaseReturn?.vendor?.address) ? (`${singlePurchaseReturn.vendor.address.line1} ${singlePurchaseReturn.vendor.address.city} ${singlePurchaseReturn.vendor.address.state}  ${singlePurchaseReturn.vendor.address.pinCode}`) : 'Address not available'
          }
          partyPhone={singlePurchaseReturn?.party?.contact?.phone?.[0]?.phoneNo || singlePurchaseReturn?.vendor?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          partyGSTIN={singlePurchaseReturn?.party?.gstNumber || singlePurchaseReturn?.vendor?.gstNumber || 'N/A'}
          tableColumns={tableColumns}
          tableData={tableData}
          tableTotals={{
            quantity: totals.quantity,
            taxableAmount: totals.taxableAmount,
            tax: totals.tax,
            amount: totals.amount
          }}
          amounts={{
            totalAmount: singlePurchaseReturn?.totalAmount,
            taxableAmount: singlePurchaseReturn?.taxableAmount,
            subtotal: singlePurchaseReturn?.subtotal,
            receivedAmount: singlePurchaseReturn?.receivedAmount,
            balanceAmount: singlePurchaseReturn?.balanceAmount
          }}
          summaryItems={summaryItems}
          customFields={customFields}
          showLoading={loading}
          showLoadingText="Fetching Purchase Return..."
          // s={singleInvoice?.s || ''}
          termsConditions={singlePurchaseReturn?.terms || ''}
          gstType={singlePurchaseReturn?.gstType}
          invoiceStatus={singlePurchaseReturn?.status}
        />
      </PrintableContainer>
    </>
  )
}

export default ShowPurchaseReturn